import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Search, Users, GraduationCap, Filter, MoreHorizontal, UserX, Ban, Lock, Unlock, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_trainer: boolean;
  is_student: boolean;
  subscription_status: string;
  subscription_plan: string;
  created_at: string;
  country: string;
  city: string;
  status: string;
  blocked_at: string;
  banned_until: string;
  deleted_at: string;
  admin_notes: string;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  trainer_id: string;
  created_at: string;
  country: string;
  city: string;
}

interface UserAction {
  userId: string;
  action: 'block' | 'unblock' | 'ban' | 'unban' | 'delete' | 'restore';
  reason: string;
  duration?: string;
}

const AdminUsers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('trainers');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: UserAction['action'];
    user: Profile | null;
  }>({ open: false, type: 'block', user: null });
  const [actionReason, setActionReason] = useState('');
  const [banDuration, setBanDuration] = useState('');

  const queryClient = useQueryClient();

  const { data: trainers, isLoading: trainersLoading } = useQuery({
    queryKey: ['admin-trainers'],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('is_trainer', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      // Get email for each trainer from auth.users
      const trainersWithEmail = await Promise.all(
        profiles.map(async (profile) => {
          const { data: userData } = await supabase.auth.admin.getUserById(profile.id);
          return {
            ...profile,
            email: userData.user?.email || ''
          };
        })
      );
      
      return trainersWithEmail as Profile[];
    }
  });

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['admin-students'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Student[];
    }
  });

  const userActionMutation = useMutation({
    mutationFn: async ({ userId, action, reason, duration }: UserAction & { duration?: string }) => {
      // Atualizar status do utilizador
      const updates: any = { admin_notes: reason };
      
      switch (action) {
        case 'block':
          updates.status = 'blocked';
          updates.blocked_at = new Date().toISOString();
          break;
        case 'unblock':
          updates.status = 'active';
          updates.blocked_at = null;
          break;
        case 'ban':
          updates.status = 'banned';
          if (duration) {
            const banUntil = new Date();
            banUntil.setDate(banUntil.getDate() + parseInt(duration));
            updates.banned_until = banUntil.toISOString();
          }
          break;
        case 'unban':
          updates.status = 'active';
          updates.banned_until = null;
          break;
        case 'delete':
          updates.status = 'deleted';
          updates.deleted_at = new Date().toISOString();
          break;
        case 'restore':
          updates.status = 'active';
          updates.deleted_at = null;
          break;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (updateError) throw updateError;

      // Registar ação no log de auditoria
      const { error: logError } = await supabase
        .from('user_actions_log')
        .insert({
          admin_id: (await supabase.auth.getUser()).data.user?.id,
          target_user_id: userId,
          action,
          reason,
          duration_until: duration ? updates.banned_until : null,
          metadata: { duration }
        });

      if (logError) throw logError;
      
      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-trainers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-students'] });
      setActionDialog({ open: false, type: 'block', user: null });
      setActionReason('');
      setBanDuration('');
      
      const actionLabels = {
        block: 'bloqueado',
        unblock: 'desbloqueado',
        ban: 'banido',
        unban: 'desbanido',
        delete: 'eliminado',
        restore: 'restaurado'
      };
      
      toast.success(`Utilizador ${actionLabels[variables.action]} com sucesso`);
    },
    onError: (error) => {
      toast.error('Erro ao executar ação: ' + error.message);
    }
  });

  const handleUserAction = (type: UserAction['action'], user: Profile) => {
    setActionDialog({ open: true, type, user });
    setActionReason('');
    setBanDuration('');
  };

  const executeAction = () => {
    if (!actionDialog.user) return;
    
    userActionMutation.mutate({
      userId: actionDialog.user.id,
      action: actionDialog.type,
      reason: actionReason,
      duration: banDuration
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'active': { label: 'Ativo', variant: 'default' as const },
      'blocked': { label: 'Bloqueado', variant: 'destructive' as const },
      'banned': { label: 'Banido', variant: 'destructive' as const },
      'deleted': { label: 'Eliminado', variant: 'outline' as const },
      'trialing': { label: 'Teste', variant: 'secondary' as const },
      'canceled': { label: 'Cancelado', variant: 'destructive' as const },
      'past_due': { label: 'Em Atraso', variant: 'destructive' as const },
      'incomplete': { label: 'Incompleto', variant: 'outline' as const }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, variant: 'outline' as const };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getPlanBadge = (plan: string) => {
    const planMap = {
      'free': { label: 'Grátis', variant: 'outline' as const },
      'basic': { label: 'Básico', variant: 'secondary' as const },
      'premium': { label: 'Premium', variant: 'default' as const },
      'pro': { label: 'Pro', variant: 'default' as const }
    };
    
    const planInfo = planMap[plan as keyof typeof planMap] || { label: plan, variant: 'outline' as const };
    return <Badge variant={planInfo.variant}>{planInfo.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredTrainers = trainers?.filter(trainer =>
    `${trainer.first_name} ${trainer.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStudents = students?.filter(student =>
    `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (trainersLoading || studentsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const getActionDialogTitle = () => {
    const titles = {
      block: 'Bloquear Utilizador',
      unblock: 'Desbloquear Utilizador',
      ban: 'Banir Utilizador',
      unban: 'Desbanir Utilizador',
      delete: 'Eliminar Utilizador',
      restore: 'Restaurar Utilizador'
    };
    return titles[actionDialog.type];
  };

  const getActionDialogDescription = () => {
    const descriptions = {
      block: 'Esta ação irá bloquear o acesso do utilizador à plataforma.',
      unblock: 'Esta ação irá restaurar o acesso do utilizador à plataforma.',
      ban: 'Esta ação irá banir o utilizador da plataforma.',
      unban: 'Esta ação irá remover o banimento do utilizador.',
      delete: 'Esta ação irá marcar o utilizador como eliminado (soft delete).',
      restore: 'Esta ação irá restaurar o utilizador eliminado.'
    };
    return descriptions[actionDialog.type];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestão de Utilizadores</h1>
          <p className="text-gray-600">Gerir personal trainers e alunos da plataforma</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Pesquisar utilizadores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filtros
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Personal Trainers</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{trainers?.length || 0}</div>
            <p className="text-xs text-gray-600">Total registados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alunos</CardTitle>
            <GraduationCap className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{students?.length || 0}</div>
            <p className="text-xs text-gray-600">Total registados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativos</CardTitle>
            <Users className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(trainers?.filter(t => t.status === 'active').length || 0) + 
               (students?.filter(s => s.status === 'active').length || 0)}
            </div>
            <p className="text-xs text-gray-600">Utilizadores ativos</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getActionDialogTitle()}</DialogTitle>
            <DialogDescription>
              {getActionDialogDescription()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {actionDialog.user && (
              <div className="text-sm">
                <strong>Utilizador:</strong> {actionDialog.user.first_name} {actionDialog.user.last_name}
              </div>
            )}
            
            {actionDialog.type === 'ban' && (
              <div>
                <Label htmlFor="duration">Duração do banimento (dias)</Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="Deixe vazio para banimento permanente"
                  value={banDuration}
                  onChange={(e) => setBanDuration(e.target.value)}
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="reason">Motivo</Label>
              <Textarea
                id="reason"
                placeholder="Descreva o motivo desta ação..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: 'block', user: null })}>
              Cancelar
            </Button>
            <Button 
              onClick={executeAction} 
              disabled={!actionReason.trim() || userActionMutation.isPending}
              variant={actionDialog.type === 'delete' || actionDialog.type === 'ban' ? 'destructive' : 'default'}
            >
              {userActionMutation.isPending ? 'A processar...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="trainers">Personal Trainers ({trainers?.length || 0})</TabsTrigger>
          <TabsTrigger value="students">Alunos ({students?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="trainers">
          <Card>
            <CardHeader>
              <CardTitle>Personal Trainers</CardTitle>
              <CardDescription>
                Lista de todos os personal trainers registados na plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Registado</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTrainers?.map((trainer) => (
                    <TableRow key={trainer.id}>
                      <TableCell className="font-medium">
                        {trainer.first_name} {trainer.last_name}
                      </TableCell>
                      <TableCell>
                        {trainer.email}
                      </TableCell>
                      <TableCell>
                        {trainer.city && trainer.country 
                          ? `${trainer.city}, ${trainer.country}`
                          : trainer.country || 'Não informado'}
                      </TableCell>
                      <TableCell>
                        {getPlanBadge(trainer.subscription_plan || 'free')}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(trainer.status || 'active')}
                      </TableCell>
                      <TableCell>
                        {formatDate(trainer.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Ações</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            
                            {trainer.status === 'active' && (
                              <>
                                <DropdownMenuItem onClick={() => handleUserAction('block', trainer)}>
                                  <Lock className="mr-2 h-4 w-4" />
                                  Bloquear
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleUserAction('ban', trainer)}>
                                  <Ban className="mr-2 h-4 w-4" />
                                  Banir
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {trainer.status === 'blocked' && (
                              <DropdownMenuItem onClick={() => handleUserAction('unblock', trainer)}>
                                <Unlock className="mr-2 h-4 w-4" />
                                Desbloquear
                              </DropdownMenuItem>
                            )}
                            
                            {trainer.status === 'banned' && (
                              <DropdownMenuItem onClick={() => handleUserAction('unban', trainer)}>
                                <Unlock className="mr-2 h-4 w-4" />
                                Desbanir
                              </DropdownMenuItem>
                            )}
                            
                            {trainer.status === 'deleted' && (
                              <DropdownMenuItem onClick={() => handleUserAction('restore', trainer)}>
                                <Unlock className="mr-2 h-4 w-4" />
                                Restaurar
                              </DropdownMenuItem>
                            )}
                            
                            {trainer.status !== 'deleted' && (
                              <DropdownMenuItem 
                                onClick={() => handleUserAction('delete', trainer)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Eliminar
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="students">
          <Card>
            <CardHeader>
              <CardTitle>Alunos</CardTitle>
              <CardDescription>
                Lista de todos os alunos registados na plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Registado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents?.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell className="font-medium">
                        {student.first_name} {student.last_name}
                      </TableCell>
                      <TableCell>{student.email}</TableCell>
                      <TableCell>
                        {student.city && student.country 
                          ? `${student.city}, ${student.country}`
                          : student.country || 'Não informado'}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(student.status || 'active')}
                      </TableCell>
                      <TableCell>
                        {formatDate(student.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminUsers;
